import { apiRequest } from './client';
import type {
  AdminBranch,
  AdminBranchInput,
  AdminEmployeeDetail,
  AdminEmployeeInput,
  AdminEmployeeListQuery,
  AdminEmployeeListResult,
  AdminPlatform,
  BranchStatus,
  CreateEmployeeResult,
  EmployeeStatus,
  ResetPasswordResult,
} from './types';

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export function listEmployees(params: AdminEmployeeListQuery): Promise<AdminEmployeeListResult> {
  return apiRequest(`/admin/employees${query({ ...params })}`);
}

export function getEmployee(id: string): Promise<AdminEmployeeDetail> {
  return apiRequest(`/admin/employees/${id}`);
}

export function createEmployee(input: AdminEmployeeInput): Promise<CreateEmployeeResult> {
  return apiRequest('/admin/employees', { method: 'POST', body: input });
}

export function updateEmployee(
  id: string,
  input: Partial<AdminEmployeeInput>,
): Promise<AdminEmployeeDetail> {
  return apiRequest(`/admin/employees/${id}`, { method: 'PATCH', body: input });
}

export function setEmployeeStatus(id: string, status: EmployeeStatus): Promise<void> {
  return apiRequest(`/admin/employees/${id}/status`, { method: 'POST', body: { status } });
}

export function resetEmployeePassword(id: string): Promise<ResetPasswordResult> {
  return apiRequest(`/admin/employees/${id}/reset-password`, { method: 'POST' });
}

export function removeEmployeeNfcCard(id: string): Promise<void> {
  return apiRequest(`/admin/employees/${id}/nfc-card`, { method: 'DELETE' });
}

export function deleteEmployee(id: string): Promise<void> {
  return apiRequest(`/admin/employees/${id}`, { method: 'DELETE' });
}

/**
 * `GET /admin/branches` is paginated like employees
 * (`packages/contracts/src/unified-access/admin.contract.ts`'s
 * `adminBranchListResponseSchema` on `feature/rebuild-auth-api`: `{ items,
 * total }`, not a bare array). `pageSize` is set high because the branches
 * screen does its own client-side search rather than paging.
 */
export function listBranches(): Promise<{ items: AdminBranch[]; total: number }> {
  return apiRequest(`/admin/branches${query({ page: 1, pageSize: 100 })}`);
}

export function createBranch(input: AdminBranchInput): Promise<AdminBranch> {
  return apiRequest('/admin/branches', { method: 'POST', body: input });
}

export function updateBranch(id: string, input: Partial<AdminBranchInput>): Promise<AdminBranch> {
  return apiRequest(`/admin/branches/${id}`, { method: 'PATCH', body: input });
}

/** Branches have their own dedicated status endpoint, same shape as employees'. */
export function setBranchStatus(id: string, status: BranchStatus): Promise<void> {
  return apiRequest(`/admin/branches/${id}/status`, { method: 'POST', body: { status } });
}

/** Seeded configuration (ui-contract.md point 6): translated names for the employees list's platform badges. */
export function listPlatforms(): Promise<AdminPlatform[]> {
  return apiRequest('/admin/platforms');
}
